'use client'
import * as React from 'react'
import { ChevronDownIcon } from 'primitives/icons'
import { tw } from 'utils/classnames'
import * as NavigationMenuComponent from 'primitives/navigation-menu'
import { Link } from '@repo/web-ui/components/link'

import { NavigationLink, NavigationLinkWithIcon } from '@web/components/navigation/navigation-items'
import type { MenuItems } from '@web/hooks/use-menu-items'
import { MenuItemType } from '@web/hooks/use-menu-items'

export function NavigationMenu({ menuItems }: { menuItems: MenuItems }) {
  //This is used to set the left position of the content, in case we need to move it to the left
  //There is an issue opened for this on radix-ui side: https://github.com/radix-ui/primitives/issues/1462
  //Maybe we can come back in a few months and remove this
  const [offset, setOffset] = React.useState<number | null>(null)
  const listRef = React.useRef<HTMLDivElement | null>(null)
  const [value, setValue] = React.useState<string | undefined>()
  const [activeTrigger, setActiveTrigger] = React.useState<HTMLButtonElement | null>(null)

  React.useEffect(() => {
    const list = listRef.current
    if (activeTrigger && list) {
      setTimeout(() => {
        const listWidth = list.offsetWidth
        const windowWidth = window.innerWidth
        const triggerOffsetLeft = activeTrigger.offsetLeft
        const triggerBoundingRect = activeTrigger.getBoundingClientRect()

        //check if the content is wider than the window - the trigger position
        if (triggerBoundingRect.left + listWidth > windowWidth) {
          //calculate with how much the content is wider than the window
          const windowWidthDiff = windowWidth - listWidth - triggerBoundingRect.left
          //if the difference is smaller than the trigger position, move the content to the left
          if (windowWidthDiff <= triggerBoundingRect.left) {
            setOffset(triggerOffsetLeft + windowWidthDiff)
            return
          }
          setOffset(-triggerBoundingRect.left)
        }
        setOffset(triggerOffsetLeft)
        //We trigger this after 10ms to make sure the width animation finished
      }, 10)
    } else if (value === '') {
      setOffset(null)
    }
  }, [activeTrigger, value])

  return (
    <NavigationMenuComponent.Root
      value={value}
      onValueChange={setValue}
      role="navigation"
      aria-label="Primary Navigation"
      className="relative z-[1] flex w-full justify-center"
    >
      <NavigationMenuComponent.List className="center m-0 flex list-none rounded-[6px]">
        {menuItems.map(item => {
          if (item.type !== MenuItemType.NO_CHILDREN) {
            return (
              <NavigationMenuComponent.Item
                className="flex items-center font-medium"
                value={item.name}
                key={item.name}
              >
                <NavigationMenuComponent.Trigger
                  ref={node => {
                    if (item.name === value && activeTrigger !== node) {
                      setActiveTrigger(node)
                    }
                  }}
                  asChild={true}
                  className={tw(
                    'text-[var(--navigation-menu-text-color,_#ffffff)] hover:bg-[var(--navigation-menu-hover-background-color,_#ffffff)] hover:text-[var(--navigation-menu-hover-text-color,_#8559F9)]',
                    'group rounded-[6px] border border-[transparent] px-[10px] pb-[5px] pt-[6px] leading-none hover:border-gray-800',
                  )}
                >
                  <Link
                    href={item.href}
                    className="flex select-none items-center justify-between gap-1 outline-none"
                  >
                    {item.name}
                    <ChevronDownIcon
                      className="h-[16px] w-[16px] text-gray-500 transition-transform group-data-[state='open']/:-rotate-180"
                      aria-hidden
                    />
                  </Link>
                </NavigationMenuComponent.Trigger>

                <NavigationMenuComponent.Content
                  key={`${item.name}-content`}
                  className={tw(
                    'data-[motion=from-start]:animate-enterFromLeft data-[motion=from-end]:animate-enterFromRight data-[motion=to-start]:animate-exitToLeft data-[motion=to-end]:animate-exitToRight',
                    'absolute left-0 top-0 w-max',
                  )}
                >
                  <ul
                    className={tw(
                      item.type === MenuItemType.CUSTOM ? 'p-4' : 'p-[10px]',
                      'm-0 flex min-w-[224px] flex-shrink-0 list-none flex-col items-center gap-1',
                    )}
                  >
                    {item.type === MenuItemType.LINKS_ONLY
                      ? item.subMenuItems.map(subItem => (
                          <NavigationLink
                            key={subItem.name}
                            name={subItem.name}
                            href={subItem.href}
                          />
                        ))
                      : null}

                    {item.type === MenuItemType.LINKS_AND_IMAGES
                      ? item.subMenuItems.map(subItem => (
                          <NavigationLinkWithIcon
                            key={subItem.name}
                            name={subItem.name}
                            href={subItem.href}
                            image={subItem.image}
                            subtitle={subItem.subtitle}
                          />
                        ))
                      : null}

                    {item.type === MenuItemType.CUSTOM ? item.content : null}
                  </ul>
                </NavigationMenuComponent.Content>
              </NavigationMenuComponent.Item>
            )
          }
          return (
            <NavigationMenuComponent.Item value={item.name} key={item.name}>
              <a
                className={tw(
                  'text-[var(--navigation-menu-text-color,_#ffffff)] hover:bg-[var(--navigation-menu-hover-background-color,_#ffffff)] hover:text-[var(--navigation-menu-hover-text-color,_#8559F9)]',
                  'flex h-full select-none items-center rounded-[6px] border border-[transparent] px-[10px] pb-[5px] pt-[6px] font-medium leading-none no-underline outline-none hover:border-gray-800',
                )}
                key={item.name}
                href={item.href}
              >
                {item.name}
              </a>
            </NavigationMenuComponent.Item>
          )
        })}
      </NavigationMenuComponent.List>
      <div className="perspective-[2000px] absolute left-0 top-full flex w-fit justify-center">
        <NavigationMenuComponent.Viewport
          ref={listRef}
          style={{
            left: `${offset}px`,
            // oxlint-disable-next-line no-negated-condition
            display: !offset ? 'none' : undefined,
          }}
          className={tw(
            'transition-[left] duration-300',
            'bg-service-white rounded-4 relative h-[var(--radix-navigation-menu-viewport-height)] w-[var(--radix-navigation-menu-viewport-width)] max-w-[100dvw] overflow-hidden border border-gray-900 shadow-[0px_10px_10px_0px_rgba(0,_0,_0,_0.04),_0px_20px_25px_-5px_rgba(0,_0,_0,_0.10)]',
          )}
        />
      </div>
    </NavigationMenuComponent.Root>
  )
}
